import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Loader2, CheckCircle2, MessageSquare } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { toast } from '../hooks/use-toast';

const API = process.env.REACT_APP_BACKEND_URL;

const Field = ({ label, name, type = 'text', value, onChange, required, autoComplete, placeholder, testId }) => (
  <label className="block">
    <span className="font-display tracking-[0.18em] text-[10px] text-neutral-500 uppercase">
      {label}{required && <span className="text-[#FF6B00]">*</span>}
    </span>
    <input
      type={type}
      name={name}
      value={value}
      onChange={onChange}
      required={required}
      autoComplete={autoComplete}
      placeholder={placeholder}
      data-testid={testId}
      style={{ colorScheme: 'light', WebkitTextFillColor: '#111', color: '#111', backgroundColor: '#ffffff' }}
      className="mt-1.5 w-full border border-neutral-300 bg-white text-neutral-900 placeholder:text-neutral-400 px-3.5 py-3 text-sm focus:border-[#FF6B00] focus:outline-none focus:ring-2 focus:ring-[#FF6B00]/15 transition-colors"
    />
  </label>
);

const Inquiry = () => {
  const navigate = useNavigate();
  const { items, subtotal, clear } = useCart();

  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    vehicle: '',
    message: '',
  });

  useEffect(() => {
    // If the user lands here with an empty cart and hasn't just submitted,
    // bounce them home — nothing to inquire about.
    if (!items.length && !submitted) {
      navigate('/');
    }
  }, [items.length, navigate, submitted]);

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast({ title: 'Missing details', description: 'Please add your name and email so we can reply.' });
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        name: form.name,
        email: form.email,
        phone: form.phone,
        vehicle: form.vehicle,
        message: form.message,
        items: items.map((it) => ({
          name: it.name,
          slug: it.slug || '',
          variant: it.variantOption || '',
          price: Number(it.price) || 0,
          quantity: Number(it.quantity) || 1,
          image: it.image || '',
        })),
      };
      const res = await fetch(`${API}/api/inquiry`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const errBody = await res.json().catch(() => ({}));
        throw new Error(errBody.detail || 'Could not send inquiry');
      }
      // Success — clear cart and switch to confirmation state.
      clear();
      setSubmitted(true);
      setSubmitting(false);
    } catch (err) {
      setSubmitting(false);
      toast({ title: 'Inquiry could not be sent', description: String(err.message || err) });
    }
  };

  if (submitted) {
    return (
      <div className="bg-white min-h-screen" style={{ colorScheme: 'light' }}>
        <section className="pt-36 pb-16">
          <div className="max-w-[680px] mx-auto px-5 sm:px-6 lg:px-10 text-center">
            <div className="inline-flex h-16 w-16 items-center justify-center rounded-full bg-[#FF6B00]/10 text-[#FF6B00] mb-6">
              <CheckCircle2 size={32} />
            </div>
            <div className="font-display tracking-[0.3em] text-xs text-[#FF6B00] font-semibold mb-3">INQUIRY RECEIVED</div>
            <h1 className="font-display text-4xl md:text-5xl tracking-tight text-neutral-900 mb-4">Thanks — we got it.</h1>
            <p className="text-neutral-600 text-base leading-relaxed max-w-md mx-auto mb-2">
              We just sent a confirmation to <strong className="text-neutral-900">{form.email}</strong>. One of our build advisors will personally reply within 1 business day with pricing, lead times, and the next steps for your build.
            </p>
            <p className="text-neutral-500 text-sm mt-6">
              Questions in the meantime? Email <a href="mailto:info@fortuneautodesign.com" className="text-[#FF6B00] underline underline-offset-2">info@fortuneautodesign.com</a>.
            </p>
            <div className="mt-10 flex flex-col sm:flex-row gap-3 justify-center">
              <Link
                to="/"
                className="inline-flex items-center justify-center gap-2 bg-[#FF6B00] hover:bg-[#E25500] text-white px-8 py-3.5 font-display tracking-[0.22em] text-xs font-bold transition-colors"
                data-testid="inquiry-success-home"
              >
                BACK TO HOME
              </Link>
              <Link
                to="/wheels"
                className="inline-flex items-center justify-center gap-2 border border-neutral-300 hover:border-neutral-900 text-neutral-700 hover:text-neutral-900 px-8 py-3.5 font-display tracking-[0.22em] text-xs font-bold transition-colors"
              >
                BROWSE MORE
              </Link>
            </div>
          </div>
        </section>
      </div>
    );
  }

  if (!items.length) return null;

  return (
    <div className="bg-white min-h-screen" style={{ colorScheme: 'light' }}>
      <section className="pt-36 md:pt-36 pb-8 border-b border-neutral-100">
        <div className="max-w-[1280px] mx-auto px-5 sm:px-6 lg:px-10">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-xs text-neutral-500 hover:text-[#FF6B00] font-display tracking-[0.18em] mt-2 md:mt-0"
            data-testid="inquiry-continue-browsing"
          >
            <ArrowLeft size={14} /> CONTINUE BROWSING
          </Link>
          <div className="flex items-center gap-3 mt-4">
            <span className="h-9 w-9 rounded-full bg-[#FF6B00]/10 flex items-center justify-center text-[#FF6B00]">
              <MessageSquare size={16} />
            </span>
            <div className="font-display tracking-[0.3em] text-xs text-[#FF6B00] font-semibold">
              BUILD INQUIRY
            </div>
          </div>
          <h1 className="font-display text-3xl sm:text-4xl md:text-5xl tracking-tight mt-3 text-neutral-900">
            SUBMIT YOUR INQUIRY
          </h1>
          <p className="text-sm text-neutral-500 mt-3 max-w-2xl leading-relaxed">
            Our parts are made-to-order &mdash; every build starts with a conversation. Drop your details below and our team will reply within 1 business day with pricing, lead times, and confirmation that everything fits your vehicle. <strong className="text-neutral-700">No payment is collected here.</strong>
          </p>
        </div>
      </section>

      <section className="py-10">
        <form
          onSubmit={onSubmit}
          className="max-w-[1280px] mx-auto px-5 sm:px-6 lg:px-10 grid lg:grid-cols-[1.4fr_1fr] gap-10 lg:gap-14"
          data-testid="inquiry-form"
        >
          {/* Left: form */}
          <div className="space-y-8">
            <fieldset>
              <legend className="font-display tracking-[0.22em] text-xs text-[#FF6B00] font-semibold mb-4 pl-3 border-l-2 border-[#FF6B00]">
                YOUR DETAILS
              </legend>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Full name" name="name" value={form.name} onChange={onChange} required autoComplete="name" testId="inq-name" />
                <Field label="Email" name="email" type="email" value={form.email} onChange={onChange} required autoComplete="email" testId="inq-email" />
                <Field label="Phone (optional)" name="phone" type="tel" value={form.phone} onChange={onChange} autoComplete="tel" testId="inq-phone" />
                <Field label="Vehicle (year / make / model)" name="vehicle" value={form.vehicle} onChange={onChange} placeholder="e.g. 2022 BMW M3" testId="inq-vehicle" />
              </div>
            </fieldset>

            <fieldset>
              <legend className="font-display tracking-[0.22em] text-xs text-[#FF6B00] font-semibold mb-4 pl-3 border-l-2 border-[#FF6B00]">
                NOTES / SPEC DETAILS (OPTIONAL)
              </legend>
              <textarea
                name="message"
                value={form.message}
                onChange={onChange}
                rows={5}
                placeholder="Finish preference, custom specs, target install date, anything we should know…"
                data-testid="inq-message"
                style={{ colorScheme: 'light', WebkitTextFillColor: '#111', color: '#111', backgroundColor: '#ffffff' }}
                className="w-full border border-neutral-300 bg-white text-neutral-900 placeholder:text-neutral-400 px-3.5 py-3 text-sm focus:border-[#FF6B00] focus:outline-none focus:ring-2 focus:ring-[#FF6B00]/15 transition-colors"
              />
            </fieldset>
          </div>

          {/* Right: inquiry summary */}
          <aside>
            <div className="bg-neutral-50 border border-neutral-200 p-6 sticky top-32">
              <div className="font-display tracking-[0.22em] text-[10px] text-[#FF6B00] font-semibold mb-3">
                INQUIRY LIST &mdash; {items.length} ITEM{items.length !== 1 ? 'S' : ''}
              </div>
              <ul className="divide-y divide-neutral-200 mb-5">
                {items.map((it) => (
                  <li key={it.lineId} className="py-3 flex gap-3" data-testid={`inq-line-${it.productId}`}>
                    {it.image && <img src={it.image} alt="" className="w-14 h-14 object-cover border border-neutral-200" />}
                    <div className="flex-1 min-w-0">
                      <div className="font-display text-[12px] tracking-wide leading-snug text-neutral-900">{it.name}</div>
                      {it.variantOption && <div className="text-[10px] text-neutral-500 mt-0.5">{it.variantOption}</div>}
                      <div className="text-[11px] text-neutral-500 mt-1">Qty {it.quantity}</div>
                    </div>
                    <div className="text-sm font-display tabular-nums text-neutral-900 whitespace-nowrap">
                      ${(it.price * it.quantity).toLocaleString()}
                    </div>
                  </li>
                ))}
              </ul>
              <div className="flex justify-between items-baseline border-t border-neutral-200 pt-4">
                <span className="font-display tracking-[0.22em] text-[11px] text-neutral-500">LISTED TOTAL</span>
                <span className="font-display text-xl tabular-nums text-neutral-900">
                  ${subtotal.toLocaleString()} <span className="text-xs text-neutral-500">USD</span>
                </span>
              </div>
              <p className="text-[11px] text-neutral-500 leading-relaxed mt-4">
                Listed pricing is a starting point. <strong className="text-neutral-900">Final pricing &amp; shipping are confirmed</strong> by our team after we review fitment, finish, and current lead times for your build.
              </p>

              <button
                type="submit"
                disabled={submitting}
                data-testid="inq-submit"
                className={`w-full mt-6 py-4 font-display tracking-[0.22em] text-sm font-bold transition-colors inline-flex items-center justify-center gap-2 ${
                  submitting
                    ? 'bg-neutral-300 text-neutral-600 cursor-not-allowed'
                    : 'bg-[#FF6B00] hover:bg-[#E25500] text-white'
                }`}
              >
                {submitting ? (
                  <><Loader2 size={16} className="animate-spin" /> SENDING…</>
                ) : (
                  'SEND INQUIRY'
                )}
              </button>

              <p className="text-[11px] text-neutral-400 text-center mt-4 leading-relaxed">
                We&rsquo;ll reply within 1 business day. No payment is collected on this form.
              </p>
            </div>
          </aside>
        </form>
      </section>
    </div>
  );
};

export default Inquiry;
